import { useTableAccessibility } from '@hooks/accessibility/useTableAccessibilityHook';
import { Button, Table } from 'antd';
import { TableProps } from 'antd/lib/table';
import { SorterResult, TableRowSelection } from 'antd/lib/table/interface';
import { AntPagination } from 'components/shared/paginatedProfiBazaTable/SieveModels';
import {
	ProfiBazaTableEvents,
	getTableEvents,
} from 'components/shared/paginatedProfiBazaTable/TableEvents';
import React, { ReactNode, useEffect } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';

interface IProps<T> {
	detailsUrl?: string;
	onRow?: (
		record: any,
		rowIndex: number
	) => void | ((record: T, rowIndex: number) => ProfiBazaTableEvents);
	setDataSource?: (data: any[]) => void;
	bordered?: boolean;
	rowSelection?: TableRowSelection<T>;
	expandedRowRender?: React.ReactNode | undefined;
	handleAntTableChange?: (
		antPagination: AntPagination,
		antFilters: Record<keyof T, string[]>,
		antSorter: SorterResult<T>
	) => void;
}

const ProfiBazaTable = <T extends any>(
	props: IProps<T> & { children?: ReactNode } & TableProps<any> &
		RouteComponentProps &
		any
) => {
	const { detailsUrl, onRow, bordered, columns, ...restProps } = props;

	useTableAccessibility(props.dataSource);

	const handleRowDoubleClick = (idRow: string) => {
		props.history.push(`${props.detailsUrl}/${idRow}`);
	};

	const handleOnRow = (record: any, rowIndex: number) => {
		let tableEvents = getTableEvents();
		if (props.detailsUrl) {
			tableEvents.onDoubleClick = (
				event: React.MouseEvent<HTMLElement, MouseEvent>
			) => {
				if (!props.rowKey) return undefined;
				return handleRowDoubleClick(
					(props.rowKey as any)(record) as string
				);
			};
		}

		return tableEvents;
	};

	const getAlternateBg = (record: any, index: number) =>
		`${index % 2 === 1 ? 'row-alternate' : ''} ${
			props.rowClassName ? (props.rowClassName as any)(record, index) : ''
		}`;

	const columnsWithEnhancedTitle = () => {
		const enhancedColumns = [...columns];
		enhancedColumns.forEach((column) => {
			if (typeof column.title === 'string') {
				if (column.sorter === true) {
					column.title = (
						<Button className="btn-filter" title="Sortuj kolumnę">
							{column.title}
						</Button>
					);
				} else {
					column.title = (
						<div
							className="column-title--accessible"
							role="columnheader"
							title="Sortuj kolumnę"
							tabIndex={0}
						>
							{column.title}
						</div>
					);
				}
			}
		});

		return enhancedColumns;
	};

	useEffect(() => {
		const elements = document.querySelectorAll(
			'[aria-label="caret-up"], [aria-label="caret-down"], [data-icon="filter"], [aria-label="filter"]'
		);

		elements.forEach((element) => {
			element?.removeAttribute('aria-label');
			element?.removeAttribute('data-icon');
		});
	}, []);

	return (
		<Table
			{...restProps}
			size="small"
			bordered={bordered}
			columns={columnsWithEnhancedTitle()}
			onChange={
				props.handleAntTableChange
					? (props.handleAntTableChange as any)
					: props.onChange
			}
			onRow={(onRow ? onRow : handleOnRow) as any}
			rowClassName={(record, index) => {
				return getAlternateBg(record, index);
			}}
		></Table>
	);
};

export default withRouter(ProfiBazaTable);
